import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { useState } from "react";
import { api } from "../lib/api";
import { formatUsd } from "../lib/format";

export function LearnPage() {
  const q = useQuery({ queryKey: ["learn"], queryFn: () => api<{ articles: { slug: string; title: string; excerpt: string }[] }>("/learn") });
  return (
    <div className="wrap">
      <div className="page-head">
        <span className="kicker">Guides</span>
        <h1>Learn</h1>
        <p>How YES/NO markets, matching, fees and settlement actually work.</p>
      </div>
      <div className="grid-2">
        {(q.data?.articles ?? []).map((a) => (
          <Link key={a.slug} to={`/learn/${a.slug}`} className="card learn-card">
            <h3>{a.title}</h3>
            <p>{a.excerpt}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function LearnArticle() {
  const { slug } = useParams();
  const q = useQuery({ queryKey: ["learn", slug], queryFn: () => api<{ article: { title: string; body: string } }>(`/learn/${slug}`) });
  if (!q.data) return <div className="wrap">Loading…</div>;
  return (
    <div className="wrap prose">
      <pre style={{ whiteSpace: "pre-wrap", fontFamily: "inherit" }}>{q.data.article.body}</pre>
    </div>
  );
}

export function LegalPage({ slug, title }: { slug: string; title?: string }) {
  const q = useQuery({ queryKey: ["page", slug], queryFn: () => api<{ page: { title: string; body: string } }>(`/pages/${slug}`) });
  return (
    <div className="wrap prose">
      <h1>{q.data?.page.title ?? title}</h1>
      <p>{q.data?.page.body}</p>
    </div>
  );
}

export function TrustPage() {
  return (
    <div className="wrap prose">
      <h1>Trust center</h1>
      <h3>How markets resolve</h3>
      <p>Every market names a source, a date and written rules. A resolver proposes an outcome with evidence. Users may dispute. Settlement then runs once through the ledger.</p>
      <h3>How funds are accounted for</h3>
      <p>Cash never moves without a balanced double-entry posting. Available, reserved and clearing accounts are reconcilable.</p>
      <h3>How fees work</h3>
      <p>The standard taker fee is 0.50% of notional unless a more specific rule applies. Fees are shown before confirmation.</p>
      <h3>Market integrity</h3>
      <p>Self-trade is prevented. Liquidity from the official LP desk is internally tagged as company risk. Volume is never fabricated.</p>
    </div>
  );
}

export function StatusPage() {
  const q = useQuery({ queryKey: ["status"], queryFn: () => api<{ components: { name: string; status: string }[] }>("/status") });
  return (
    <div className="wrap">
      <h1>System status</h1>
      {(q.data?.components ?? []).map((c) => (
        <div key={c.name} className="card" style={{ marginBottom: 8, display: "flex", justifyContent: "space-between" }}>
          <span>{c.name}</span><strong className="up">{c.status}</strong>
        </div>
      ))}
    </div>
  );
}

export function LeaderboardPage() {
  const q = useQuery({ queryKey: ["lb"], queryFn: () => api<{ leaders: { username: string; realized: number; markets_traded: number }[] }>("/leaderboard") });
  return (
    <div className="wrap">
      <h1>Leaderboard</h1>
      <p>Public profiles only. Private balances are never shown here.</p>
      <div className="table-scroll"><table className="table">
        <thead><tr><th>Trader</th><th>Realized P/L (public)</th><th>Markets</th></tr></thead>
        <tbody>
            {(q.data?.leaders ?? []).map((r) => (
              <tr key={r.username}><td><Link to={`/u/${r.username}`}>{r.username}</Link></td><td>{formatUsd(r.realized)}</td><td>{r.markets_traded}</td></tr>
            ))}
        </tbody>
      </table></div>
    </div>
  );
}

export function SuggestPage() {
  const [question, setQuestion] = useState("");
  const [source, setSource] = useState("");
  const [rules, setRules] = useState("");
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState("");
  return (
    <div className="wrap prose">
      <h1>Suggest a market</h1>
      <p>Users cannot publish live markets directly. An admin reviews resolution source, date and rules first.</p>
      <form className="card" onSubmit={async (e) => {
        e.preventDefault();
        setErr("");
        try {
          await api("/suggest", { method: "POST", body: JSON.stringify({ question, suggestedSource: source, resolutionCriteria: rules }) });
          setOk(true);
        } catch (ex) {
          setErr((ex as Error).message);
        }
      }}>
        <label className="field">Question<input value={question} onChange={(e) => setQuestion(e.target.value)} required minLength={10} /></label>
        <label className="field">Suggested source<input value={source} onChange={(e) => setSource(e.target.value)} /></label>
        <label className="field">Resolution criteria<textarea value={rules} onChange={(e) => setRules(e.target.value)} /></label>
        {err && <p style={{ color: "var(--danger)" }}>{err}</p>}
        <button className="btn btn-primary">Submit for review</button>
        {ok && <p>Submitted. Thank you.</p>}
      </form>
    </div>
  );
}

export function HelpPage() {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [ok, setOk] = useState(false);
  return (
    <div className="wrap prose">
      <h1>Help</h1>
      <p>Start with Learn, then contact support if something in your account looks wrong.</p>
      <form className="card" onSubmit={async (e) => {
        e.preventDefault();
        await api("/tickets", { method: "POST", body: JSON.stringify({ category: "general", subject, body }) });
        setOk(true);
      }}>
        <label className="field">Subject<input value={subject} onChange={(e) => setSubject(e.target.value)} required /></label>
        <label className="field">Message<textarea value={body} onChange={(e) => setBody(e.target.value)} required /></label>
        <button className="btn btn-primary">Send ticket</button>
        {ok && <p>Ticket opened.</p>}
      </form>
    </div>
  );
}
